import { logger } from 'logger';
import { getVegaOsApi } from 'utils/vegaos/VegaOsApi';
import { getWebOSApi, WebOSError } from './webos/webOSApi';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOG = logger.getLogger('getDevice');

export enum Device {
  androidtv = 'androidtv',
  firetv = 'firetv',
  xbox = 'xbox',
  lgtv = 'lgtv',
}

export enum DeviceSubtype {
  none = 'none',
  vegaos = 'vegaos',
}

export const metaDevices: Record<Device, string> = {
  [Device.xbox]: 'Xbox',
  [Device.androidtv]: 'AndroidTV',
  [Device.firetv]: 'FireTV',
  [Device.lgtv]: 'LG TV',
};

type CachedValues = {
  adId: string;
  adIdDirty: boolean;
  device: Device;
  deviceSubtype: DeviceSubtype;
  deviceDirty: boolean;
  deviceSubtypeDirty: boolean;
  deviceCaptionsEnabled?: boolean | null;
  osVersion?: string | undefined;
  webosDeviceInfo?: DeviceInfo;
  deviceId: string;
  modelName: string;
};

const cachedValues: CachedValues = {
  adId: '',
  adIdDirty: true,
  device: Device.xbox,
  deviceSubtype: DeviceSubtype.none,
  deviceDirty: true,
  deviceSubtypeDirty: true,
  deviceId: '',
  modelName: '',
};

export function updateCachedWebOsDeviceInfo(deviceInfo: DeviceInfo) {
  cachedValues.webosDeviceInfo = deviceInfo;
}

function _getDeviceID(): string {
  if (cachedValues.deviceId !== '' && cachedValues.deviceId != null) {
    return cachedValues.deviceId;
  }

  let deviceId = '';
  if (self.UWP && self.UWP.deviceID) {
    deviceId = self.UWP.deviceID;
  } else if (self.Android !== undefined) {
    const android = self.Android;
    deviceId = android.getDeviceId();
  } else if (isVegaOS()) {
    deviceId = getVegaOsApi()?.getBuildInfo?.()?.deviceId ?? '';
  } else if (isLgTv()) {
    getWebOSApi()
      ?.getDeviceId()
      .then((result) => (cachedValues.deviceId = result))
      .catch((reason: WebOSError) => LOG.error(`Error code: ${reason.errorCode}\nError Message: ${reason.errorText}`));
  }
  cachedValues.deviceId = deviceId;
  return deviceId;
}

function _getDeviceSubtype(): DeviceSubtype {
  if (isVegaOS()) {
    return DeviceSubtype.vegaos;
  }

  return DeviceSubtype.none;
}

function _getAdID(): string {
  let adId = '';
  if (self.UWP && self.UWP.adID) {
    adId = self.UWP.adID;
  } else if (self.Android !== undefined) {
    adId = self.Android.getAdvertisingId();
  } else if (isVegaOS()) {
    adId = getVegaOsApi()?.getAdId?.() ?? '';
  }

  return adId;
}

function _getDevice(): Device {
  if (isAndroid()) {
    const android = self.Android;
    if (android.getDeviceType().toLowerCase() === 'firetv') {
      return Device.firetv;
    } else if (android.getDeviceType().toLowerCase() === 'androidtv') {
      return Device.androidtv;
    }
  } else if (isLgTv()) {
    LOG.info('successful webOS detection');
    return Device.lgtv;
  } else if (isVegaOS()) {
    LOG.info('successful vegaOS detection');
    return Device.firetv;
  }

  return Device.xbox;
}

function isAndroid(): boolean {
  return self.Android !== undefined;
}

function isVegaOS(): boolean {
  return self.ReactNativeWebView !== undefined;
}

function isLgTv(): boolean {
  return /web0s/i.test(self.navigator.userAgent);
}

export function isXbox(): boolean {
  return self.UWP !== undefined || Object.keys(self.UWP).length > 0;
}

export function getDevice(): Device {
  if (!cachedValues.deviceDirty) {
    return cachedValues.device;
  }
  const device = _getDevice();
  cachedValues.device = device;
  cachedValues.deviceDirty = false;

  return device;
}

export function getDeviceSubtype(): DeviceSubtype {
  if (!cachedValues.deviceSubtypeDirty) {
    return cachedValues.deviceSubtype;
  }
  const deviceSubtype = _getDeviceSubtype();
  cachedValues.deviceSubtype = deviceSubtype;
  cachedValues.deviceSubtypeDirty = false;

  LOG.debug('device subtype', DeviceSubtype.vegaos);

  return deviceSubtype;
}

export function getDeviceID(): string {
  const deviceID = _getDeviceID();
  return deviceID;
}

export function getAdID(): string {
  if (!cachedValues.adIdDirty) {
    return cachedValues.adId;
  }
  cachedValues.adId = _getAdID();
  cachedValues.adIdDirty = false;

  return cachedValues.adId;
}

export function invalidateAdId() {
  cachedValues.adIdDirty = true;
}

function _getModelName(): string {
  let modelName = '';
  if (self.UWP && self.UWP.deviceID) {
    modelName = self.UWP.model ?? '';
  } else if (self.Android !== undefined) {
    const android = self.Android;
    modelName = android.getModelName();
  } else if (isVegaOS()) {
    modelName = getVegaOsApi()?.getBuildInfo?.()?.model ?? '';
  } else if (isLgTv()) {
    modelName = cachedValues.webosDeviceInfo?.modelName ?? '';
  }
  LOG.debug(`modelName: ${modelName}`);
  cachedValues.modelName = modelName;
  return modelName;
}

export function getModelName(): string {
  if (cachedValues.modelName !== '' && cachedValues.modelName != null) {
    return cachedValues.modelName;
  }

  return _getModelName();
}

function _getOsVersion(): string | undefined {
  switch (getDevice()) {
    case Device.androidtv:
    case Device.firetv:
      if (getDeviceSubtype() === DeviceSubtype.vegaos) {
        cachedValues.osVersion = getVegaOsApi()?.getBuildInfo?.()?.systemVersion;
        return cachedValues.osVersion;
      } else {
        cachedValues.osVersion = self.Android?.getApiLevel?.();
        return cachedValues.osVersion;
      }
    case Device.lgtv:
      cachedValues.osVersion = cachedValues.webosDeviceInfo?.sdkVersion;
      return cachedValues.osVersion;
    case Device.xbox:
      cachedValues.osVersion = self.UWP.osVersion;
      return cachedValues.osVersion;
    default:
      return undefined; // not available on this platform
  }
}

export function getOsVersion(): string | undefined {
  if (cachedValues.osVersion != null) {
    return cachedValues.osVersion;
  }

  return _getOsVersion();
}

function _getDeviceCaptionsEnabled(): CachedValues['deviceCaptionsEnabled'] {
  switch (getDevice()) {
    case Device.androidtv:
    case Device.firetv:
      if (getDeviceSubtype() === DeviceSubtype.vegaos) {
        cachedValues.deviceCaptionsEnabled = getVegaOsApi()?.getClosedCaptionsEnabled?.();
        return cachedValues.deviceCaptionsEnabled;
      } else {
        cachedValues.deviceCaptionsEnabled = self.Android.getSystemCaptionsEnabled();
        return cachedValues.deviceCaptionsEnabled;
      }
    case Device.lgtv:
      getWebOSApi()
        ?.getSystemCaptionsEnabled()
        .then((result) => (cachedValues.deviceCaptionsEnabled = result))
        .catch((reason: WebOSError) =>
          LOG.error(
            `Error code: ${(reason as WebOSError).errorCode}\nError Message: ${(reason as WebOSError).errorText}`,
          ),
        );
      return cachedValues.deviceCaptionsEnabled;

    /**
     * currently not supported on xbox
     * @see {@link https://stackoverflow.com/questions/42649887/check-if-user-enabled-closed-captions-in-uwp-xbox-app}
     */
    case Device.xbox:
      return null;

    default:
      return null; // not available on this platform
  }
}

export function getDeviceCaptionsEnabled() {
  if (cachedValues.deviceCaptionsEnabled != null) {
    return cachedValues.deviceCaptionsEnabled;
  }

  return _getDeviceCaptionsEnabled();
}
